import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@jywer/ui';
import { CreateTemplateForm } from './CreateTemplateForm';
import { TemplateEditor } from './TemplateEditor';
import { listTemplates, type ChecklistTemplate, type ProjectType } from './api';

const PROJECT_TYPE_LABEL: Record<ProjectType, string> = {
  website: 'Website',
  video: 'Video',
  pakkelosning: 'Pakkeløsning',
  klippekort: 'Klippekort',
};

const PROJECT_TYPE_ORDER: ProjectType[] = ['website', 'video', 'pakkelosning', 'klippekort'];

export function ChecklistTemplatesPage() {
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const rows = await listTemplates();
      setTemplates(rows);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const grouped = useMemo(() => {
    const out: Record<ProjectType, ChecklistTemplate[]> = {
      website: [],
      video: [],
      pakkelosning: [],
      klippekort: [],
    };
    for (const t of templates) out[t.project_type].push(t);
    return out;
  }, [templates]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="mb-1 text-2xl font-semibold text-primary">Checklist-skabeloner</h1>
          <p className="text-sm text-ink-2">
            Per projekttype + fase. Når et projekt går ind i en fase, klones skabelonen til en
            instans på projektet.
          </p>
        </div>
        {!creating ? <Button onClick={() => setCreating(true)}>+ Opret skabelon</Button> : null}
      </div>

      {creating ? (
        <CreateTemplateForm
          onCancel={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            await refresh();
          }}
        />
      ) : null}

      {err ? (
        <div className="rounded border border-status-err/40 bg-status-err/5 px-4 py-3 text-sm text-status-err">
          {err}
        </div>
      ) : null}

      {loading && templates.length === 0 ? (
        <div className="rounded border border-line-1 bg-paper-0 px-4 py-10 text-center text-ink-2">
          Henter skabeloner…
        </div>
      ) : templates.length === 0 ? (
        <div className="rounded border border-line-1 bg-paper-0 px-4 py-10 text-center text-ink-2">
          Ingen skabeloner endnu. Opret den første ovenfor.
        </div>
      ) : (
        PROJECT_TYPE_ORDER.map((type) => {
          const rows = grouped[type];
          if (rows.length === 0) return null;
          return (
            <section key={type} className="flex flex-col gap-3">
              <h2 className="text-h2 font-medium text-ink-0">{PROJECT_TYPE_LABEL[type]}</h2>
              <div className="flex flex-col gap-3">
                {rows.map((t) => (
                  <TemplateEditor key={t.id} template={t} onChanged={refresh} />
                ))}
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}
